import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { describeError } from '../describeError';
import { useIsMounted } from '../hooks/useIsMounted';
import { payrollHistoryImportGateway } from '../index';
import { IMPORT_BASE } from '../routes';
import { saveDownloadedFile } from '../saveBlob';
import type { Capabilities, ProviderInfo, TemplateKind } from '../types';
import { downloadsFor } from '../downloads';
import { ErrorSummary } from './basics';

/** Colour is never the only cue: the label text and an icon both say how well EPN understands the format. */
function SupportBadge({ provider, id }: { provider: ProviderInfo; id: string }) {
  const tone = provider.supportLevel === 'NativeTemplate' ? 'native' : provider.supportLevel === 'GuidedMapping' ? 'guided' : 'unverified';
  const icon = tone === 'native' ? '✓' : tone === 'guided' ? '▦' : '?';
  return (
    <span id={id} className={`phi-support phi-support--${tone}`} data-testid={`support-label-${provider.id}`}>
      <span aria-hidden="true">{icon}</span>
      {provider.supportLabel}
    </span>
  );
}

function ProviderExplanation({ provider }: { provider: ProviderInfo }) {
  if (provider.supportLevel === 'NativeTemplate') return null;
  return (
    <div className="phi-notice" data-testid="provider-explanation">
      {provider.supportLevel === 'NotVerified' ? (
        <>
          <p>
            EPN has not verified {provider.displayName}&apos;s export format, so nothing is assumed about how its file is laid out.
          </p>
          <p>
            You can still upload a spreadsheet or CSV export from {provider.displayName} and tell EPN what each column means.
          </p>
        </>
      ) : (
        <p>Upload a spreadsheet or CSV register from any payroll system and tell EPN what each column means.</p>
      )}
      <p className="muted">EPN will suggest what each column might be, but nothing is used until you confirm it.</p>
    </div>
  );
}

interface Props {
  capabilities: Capabilities;
  announce: (message: string) => void;
  /** Provider pre-selected (from the address), if it is one of the available providers. */
  initialProviderId: string | null;
  /** Starts the import batch and moves to the upload step. Resolves to an error message, or null when it navigated. */
  onStart: (provider: ProviderInfo) => Promise<string | null>;
}

export function ProviderStep({ capabilities, announce, initialProviderId, onStart }: Props) {
  const mounted = useIsMounted();
  const providers = capabilities.providers;
  const [selectedId, setSelectedId] = useState<string>(
    () => providers.find((p) => p.id === initialProviderId)?.id ?? providers[0]?.id ?? '',
  );
  const [downloading, setDownloading] = useState<TemplateKind | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = providers.find((p) => p.id === selectedId);

  const choose = (provider: ProviderInfo) => {
    setSelectedId(provider.id);
    setError(null);
    announce(`${provider.displayName} selected. ${provider.supportLabel}.`);
  };

  const download = async (kind: TemplateKind) => {
    setDownloading(kind);
    setError(null);
    try {
      const file = await payrollHistoryImportGateway.downloadTemplate(kind);
      saveDownloadedFile(file);
      if (mounted.current) announce(`Downloaded ${file.fileName}.`);
    } catch (err) {
      if (mounted.current) setError(describeError(err, 'Could not download this file.'));
    } finally {
      if (mounted.current) setDownloading(null);
    }
  };

  const start = async () => {
    if (!selected) return;
    setStarting(true);
    setError(null);
    const failure = await onStart(selected);
    if (failure && mounted.current) {
      setError(failure);
      setStarting(false);
    }
  };

  if (providers.length === 0) {
    return (
      <div>
        <p>No payroll sources are available to import from right now.</p>
        <div className="phi-footer">
          <Link to={IMPORT_BASE} className="phi-btn-secondary">Back to import history</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p>Choose where your payroll history comes from. This tells EPN how to read the file you upload next.</p>
      {error && <ErrorSummary message={error} />}
      <fieldset className="phi-fieldset">
        <legend>Where is your payroll history from?</legend>
        <ul className="phi-provider-list">
          {providers.map((p) => {
            const checked = p.id === selectedId;
            const badgeId = `phi-provider-${p.id}-support`;
            const descId = `phi-provider-${p.id}-desc`;
            return (
              <li key={p.id} className={`phi-provider-card${checked ? ' phi-provider-card--selected' : ''}`} data-testid={`provider-${p.id}`}>
                <input
                  type="radio"
                  name="phi-provider"
                  id={`phi-provider-${p.id}`}
                  value={p.id}
                  checked={checked}
                  aria-describedby={`${badgeId} ${descId}`}
                  onChange={() => choose(p)}
                />
                <div className="phi-provider-body">
                  <label htmlFor={`phi-provider-${p.id}`} className="phi-provider-name">{p.displayName}</label>
                  <SupportBadge provider={p} id={badgeId} />
                  <p id={descId}>{p.description}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </fieldset>

      {selected && (
        <section aria-labelledby="phi-provider-detail-heading">
          <h3 id="phi-provider-detail-heading">About {selected.displayName}</h3>
          <ProviderExplanation provider={selected} />
          {downloadsFor(selected).length > 0 && (
            <>
              <p>Download the template to prepare your file, or an example to see a finished one.</p>
              <ul className="phi-download-list">
                {downloadsFor(selected).map((d) => (
                  <li key={d.kind} className="phi-download-item">
                    <div>
                      <strong>{d.label.replace(/^Download /, '')}</strong>
                      <p className="muted">{d.description}</p>
                    </div>
                    <Button
                      type="button"
                      className="phi-btn-secondary"
                      data-testid={`download-${d.kind}`}
                      onClick={() => void download(d.kind)}
                      loading={downloading === d.kind}
                      loadingLabel="Downloading…"
                      disabled={downloading !== null && downloading !== d.kind}
                    >
                      {d.label}
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      <div className="phi-footer">
        <Link to={IMPORT_BASE} className="phi-btn-secondary">Back to import history</Link>
        <Button type="button" disabled={!selected} onClick={() => void start()} loading={starting} loadingLabel="Starting import…">
          Continue
        </Button>
      </div>
    </div>
  );
}
